import React, {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import MetricForm from './MetricForm';
import CurrentUserContext from '../../../../../../contexts/CurrentUserContext';
import DatasetDefinitionsContext from '../../../../../../contexts/DatasetDefinitionsContext';
import AnalyticsContext from '../../../../../../contexts/AnalyticsContext';
import DateInputContext from '../../../../../../contexts/DateInputContext';
import fiToDrillDowns from './fiToDrillDowns';
import getTimeStamp from '../../../../../../getTimeStamp';
import FilterPlatesProvider from '../../../../../../contextProviders/FilterPlatesProvider';
import ReactPortal from '../../../../../../components/ReactPortal';
import { MetricPopupPortalIdsContext } from '../../MetricPopupPortalIdsContext';
import Preview from '../../Preview';
import BonusMetricAlert from '../BonusMetricAlert';
import MetricActions from '../../MetricActions';
import buildFilterInput from '../../../../../../utils/buildFilterInput';
import withoutNulls from '../../../../../../api/search/withoutNulls';
import useDataSourceOptions from './useDataSourceOptions';
import useFieldOptions from './useFieldOptions';
import useAggFuncOptions from './useAggFuncOptions';
import useFieldDef from './useFieldDef';
import CoreToggle from '../CoreToggle';
import useEditingState from '../../MetricActions/useEditingState';
import filterPlateTypeCheckers from 'types/filterPlateTypeCheckers';
import SavedFiltersContext from 'screens/DataManager/DatasetFilters/SavedFilters/context/SavedFiltersContext';
import { savedFilterToPlate } from 'contextProviders/SplashScreenProviders/DatasetProviders/DatasetFiltersProvider';
import useToFixedFilters from 'hooks/useToFixedFilters';
import DuplicationWarning from './DuplicationWarning';
import withDateFilter from '../../../../../../hocs/withDateFIlter';
import { LAST_365_DAYS_INCLUDING_TODAY } from '../../../../../../components/DateInput/constants';

const toPlates = (drillDowns: FixedFilter[]): FilterPlateType[] => {
  return drillDowns.map((d) => ({
    id: d.id,
    type: 'Fixed',
    fixedValue: d,
  }));
};

const MetricFormContainer = ({
  id,
  selectedMetric,
  forcedDataset,
  showFlash,
  setHasUnsavedChanges,
}: {
  id: string;
  selectedMetric?: Metrics.NormalMetric;
  forcedDataset?: string;
  showFlash: (message: string) => void;
  setHasUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { previewDivId, primaryActionDivId, coreToggleDivId } = useContext(
    MetricPopupPortalIdsContext,
  );
  const currentUser = useContext(CurrentUserContext);
  const { datasets } = useContext(DatasetDefinitionsContext);
  const { trackEvent } = useContext(AnalyticsContext);
  const { dataTypes, setDataTypes } = useContext(DateInputContext);
  const { savedFilters } = useContext(SavedFiltersContext);
  const toDrillDowns = useToFixedFilters();

  const [name, setName] = useState<string>(
    selectedMetric ? selectedMetric.name : '',
  );
  const [description, setDescription] = useState<string>(
    selectedMetric && selectedMetric.description
      ? selectedMetric.description
      : '',
  );
  const [status, setStatus] = useState<Metrics.MetricStatus>(
    selectedMetric ? selectedMetric.status : 'public',
  );
  const [formatting, setFormatting] = useState<MetricFormatting>(
    selectedMetric && selectedMetric.formatting
      ? selectedMetric.formatting
      : {},
  );
  const [dataSource, setDataSource] = useState<string | undefined>(
    selectedMetric ? selectedMetric.dataType : forcedDataset,
  );
  const [field, setField] = useState<string>(
    selectedMetric ? selectedMetric.field : '',
  );
  const fieldDef = useFieldDef({ field, selectedMetric });
  const [aggFunc, setAggFunc] = useState<AggFunc | undefined>(
    selectedMetric ? selectedMetric.aggFunc : undefined,
  );
  const [drillDowns, setDrillDowns] = useState<FilterPlateType[]>(() =>
    selectedMetric
      ? [
          ...toPlates(fiToDrillDowns(selectedMetric.filters, datasets)),
          ...savedFilters
            .filter((df) => selectedMetric.datasetFilterIds?.includes(df.id))
            .map(savedFilterToPlate),
        ]
      : [],
  );

  const [filterInput, setFilterInput] = useState<FilterInput>(() =>
    buildFilterInput({
      scopes: [],
      drillDowns: toDrillDowns({
        plates: drillDowns.filter(filterPlateTypeCheckers.isNotSavedFilter),
        variableDrillDowns: [],
      }),
    }),
  );

  const [isValid, setIsValid] = useState<boolean>(false);
  const [metricDraft, setMetricDraft] = useState<
    Metrics.NormalMetric | undefined
  >();
  const editingState = useEditingState({ selectedMetric, metricDraft });
  useEffect(() => {
    if (selectedMetric) {
      setHasUnsavedChanges(
        editingState !== 'no changes' &&
          editingState !== 'no changes - archived',
      );
      return;
    }

    setHasUnsavedChanges(
      name !== '' ||
        description !== '' ||
        dataSource !== (forcedDataset ? forcedDataset : undefined) ||
        field !== '' ||
        aggFunc !== undefined ||
        drillDowns.length > 0,
    );
  }, [
    aggFunc,
    dataSource,
    description,
    drillDowns.length,
    editingState,
    field,
    forcedDataset,
    name,
    selectedMetric,
    setHasUnsavedChanges,
  ]);

  const onDatasetChanged = useCallback(() => {
    setField('');
    setAggFunc(undefined);
  }, []);
  const dataSourceOptions = useDataSourceOptions({
    setDataSource,
    onDatasetChanged,
    setDrillDowns,
  });
  const fieldOptions = useFieldOptions({
    selectedDataset: dataSource,
    selectedMetric,
    setField,
    aggFunc,
    setAggFunc,
  });
  const aggFuncOptions = useAggFuncOptions({
    selectedDataset: dataSource,
    field,
    setAggFunc,
  });

  useEffect(() => {
    setDataTypes(dataSource ? [dataSource] : window.emptyArray);
  }, [dataSource, setDataTypes]);

  const onNameChanged = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };

  const onDescriptionChanged = (event: ChangeEvent<HTMLInputElement>) => {
    setDescription(event.target.value);
  };

  const onAddFilterClicked = useCallback(() => {
    trackEvent('Metric Builder - Add filter clicked');
  }, [trackEvent]);

  const onFilterAdded = useCallback(() => {
    trackEvent('Metric Builder - Filter added');
  }, [trackEvent]);

  const onFilterRemoved = useCallback(() => {
    trackEvent('Metric Builder - Filter removed');
  }, [trackEvent]);

  useEffect(() => {
    setIsValid(
      name !== '' && dataSource !== '' && field !== '' && aggFunc !== undefined,
    );
  }, [aggFunc, dataSource, field, name]);

  useEffect(() => {
    if (!isValid || !dataSource || !aggFunc) {
      setMetricDraft(undefined);
      return;
    }

    const datasetFilterIds = drillDowns
      .filter(filterPlateTypeCheckers.isSavedFilter)
      .map((dsFilter) => dsFilter.id);

    const newMetric: Metrics.NormalMetric = {
      id,
      name,
      description,
      dataType: dataSource,
      field,
      status,
      aggFunc,
      filters: filterInput,
      datasetFilterIds,
      formatting,
      type: 'normal',
      createdBy: selectedMetric ? selectedMetric.createdBy : currentUser.id,
      createdOn: selectedMetric ? selectedMetric.createdOn : getTimeStamp(),
      updatedBy: currentUser.id,
      updatedOn: getTimeStamp(),
    };
    setMetricDraft(withoutNulls(newMetric));
  }, [
    aggFunc,
    currentUser.id,
    dataSource,
    description,
    field,
    fieldDef,
    filterInput,
    formatting,
    id,
    isValid,
    name,
    selectedMetric,
    status,
    drillDowns,
  ]);

  useEffect(() => {
    setFilterInput(
      buildFilterInput({
        scopes: [],
        drillDowns: toDrillDowns({
          plates: drillDowns.filter(filterPlateTypeCheckers.isNotSavedFilter),
          variableDrillDowns: [],
        }),
      }),
    );
  }, [drillDowns, toDrillDowns]);

  return (
    <FilterPlatesProvider
      drillDowns={drillDowns}
      setDrillDowns={setDrillDowns}
      dataTypes={dataTypes}
    >
      <>
        <MetricForm
          name={name}
          onNameChanged={onNameChanged}
          description={description}
          onDescriptionChanged={onDescriptionChanged}
          formatting={formatting}
          setFormatting={setFormatting}
          dataSource={dataSource}
          dataSourceOptions={dataSourceOptions}
          field={field}
          fieldOptions={fieldOptions}
          aggFunc={aggFunc}
          aggFuncOptions={aggFuncOptions}
          drillDowns={drillDowns}
          onAddFilterClicked={onAddFilterClicked}
          onFilterAdded={onFilterAdded}
          onFilterRemoved={onFilterRemoved}
          isDatasetDisabled={!!forcedDataset}
          metricDraft={metricDraft}
        />
        <ReactPortal elementId={previewDivId}>
          {!!metricDraft && (
            <Preview metricDraft={metricDraft} isValid={isValid} />
          )}
          <BonusMetricAlert />
          {!!metricDraft && isValid && (
            <DuplicationWarning metricDraft={metricDraft} />
          )}
        </ReactPortal>
        <ReactPortal elementId={primaryActionDivId}>
          <MetricActions
            metricDraft={metricDraft}
            selectedMetric={selectedMetric}
            setStatus={setStatus}
            showFlash={showFlash}
          />
        </ReactPortal>
        <ReactPortal elementId={coreToggleDivId}>
          <CoreToggle
            status={status}
            setStatus={setStatus}
            selectedMetric={selectedMetric}
          />
        </ReactPortal>
      </>
    </FilterPlatesProvider>
  );
};

export default withDateFilter(MetricFormContainer, {
  initialRelativeDateRange: LAST_365_DAYS_INCLUDING_TODAY,
});
